import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Send, CheckCircle2, XCircle, Search, Eye, RefreshCw, Loader2, Rocket, PenLine, Clock, Ban, Megaphone, UserMinus } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import EmailComposer from "@/components/EmailComposer";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const CAT_LABELS = {
  waitlist_confirmation: "Waitlist confirmation",
  provider_confirmation: "Provider confirmation",
  launch_announcement: "Launch announcement",
  broadcast: "Broadcast",
  test: "Test send",
  general: "General",
};
const catLabel = (c) => CAT_LABELS[c] || c;

const LAUNCH_BODY = `<p style="margin:0 0 14px;">Big news — <strong>SOSConnect AI is now live in your city!</strong></p>
<p style="margin:0 0 14px;">You were one of the first to request us, so you get first access. Open the app, connect to a nearby verified pro in seconds, and let our AI handle the rest.</p>
<p style="margin:0 0 14px;">Thanks for helping bring intelligent emergency help to your neighborhood. Welcome aboard. 🎉</p>`;

const launchPreset = {
  title: "Launch announcement",
  category: "launch_announcement",
  subject: "SOSConnect AI just launched in your city 🎉",
  body: LAUNCH_BODY,
  audience: { type: "waitlist", city: "", state: "", user_type: "all" },
};

const composePreset = {
  title: "Compose email",
  category: "broadcast",
  subject: "",
  body: "<p>Hi there,</p><p></p>",
  audience: { type: "waitlist", city: "", state: "", user_type: "all" },
};

const STATUS_STYLE = {
  sent: "bg-ai/10 text-ai",
  failed: "bg-sos/10 text-sos",
  skipped: "bg-amber-500/10 text-amber-600",
};

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-2xl border border-black/5 bg-white p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-neutral-500">{label}</span>
        <Icon className={`h-4 w-4 ${tone || "text-neutral-300"}`} />
      </div>
      <p className="mt-2 text-3xl font-bold tracking-tight text-neutral-900">{value?.toLocaleString?.() ?? value ?? 0}</p>
    </div>
  );
}

export default function EmailLog() {
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ rows: [], total: 0 });
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [preview, setPreview] = useState(null);
  const [resending, setResending] = useState(null);
  const [composer, setComposer] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [canceling, setCanceling] = useState(null);

  const loadStats = () => api.get("/admin/emails/stats").then((r) => setStats(r.data)).catch(() => {});
  const loadCampaigns = () => api.get("/admin/emails/campaigns").then((r) => setCampaigns(r.data.campaigns || [])).catch(() => {});
  const load = () => api.get("/admin/emails", { params: { search, category, status, page_size: 50 } })
    .then((r) => setData(r.data)).catch(() => {});

  useEffect(() => { loadStats(); loadCampaigns(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, status]);

  const refreshAll = () => { load(); loadStats(); loadCampaigns(); };

  const cancelCampaign = async (id) => {
    setCanceling(id);
    try { await api.post(`/admin/emails/campaigns/${id}/cancel`); toast.success("Scheduled send canceled"); loadCampaigns(); }
    catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
    finally { setCanceling(null); }
  };

  const openPreview = async (id) => {
    try { const r = await api.get(`/admin/emails/${id}`); setPreview(r.data); }
    catch { toast.error("Could not load email"); }
  };

  const resend = async (id) => {
    setResending(id);
    try {
      const r = await api.post(`/admin/emails/${id}/resend`);
      if (r.data.success) toast.success("Email resent");
      else toast.error(`Resend ${r.data.status}: ${r.data.error || ""}`);
      load(); loadStats();
    } catch { toast.error("Resend failed"); }
    finally { setResending(null); }
  };

  return (
    <div data-testid="email-log">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-neutral-900">Emails</h2>
          <p className="text-sm text-neutral-500">Announce launches and send fully custom campaigns.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setComposer(launchPreset)} className="h-11 gap-2 rounded-xl bg-ai hover:bg-ai/90" data-testid="launch-announce-btn">
            <Rocket className="h-4 w-4" /> Launch Announce
          </Button>
          <Button onClick={() => setComposer(composePreset)} variant="outline" className="h-11 gap-2 rounded-xl" data-testid="compose-email-btn">
            <PenLine className="h-4 w-4" /> Compose Email
          </Button>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        <Stat icon={Mail} label="Total emails" value={stats?.total} />
        <Stat icon={CheckCircle2} label="Delivered" value={stats?.sent} tone="text-ai" />
        <Stat icon={XCircle} label="Failed / skipped" value={stats?.failed} tone="text-sos" />
        <Stat icon={Send} label="Types" value={stats?.categories?.length} />
        <Stat icon={UserMinus} label="Unsubscribed" value={stats?.unsubscribed} tone="text-amber-500" />
      </div>

      {campaigns.length > 0 && (
        <div className="mb-6 overflow-hidden rounded-2xl border border-black/5 bg-white" data-testid="campaigns-panel">
          <div className="flex items-center gap-2 border-b border-black/5 px-5 py-3">
            <Megaphone className="h-4 w-4 text-brand" />
            <h3 className="text-sm font-bold text-neutral-800">Campaigns &amp; scheduled sends</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead>
                <TableHead>Recipients</TableHead><TableHead>Delivered</TableHead><TableHead>Opens</TableHead>
                <TableHead>When</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.map((c) => (
                <TableRow key={c.id} data-testid="campaign-row">
                  <TableCell className="max-w-[220px] truncate font-medium">{c.subject}</TableCell>
                  <TableCell><Badge variant="secondary">{catLabel(c.category)}</Badge></TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${
                      c.status === "scheduled" ? "bg-amber-500/10 text-amber-600" :
                      c.status === "sending" ? "bg-brand/10 text-brand" :
                      c.status === "canceled" ? "bg-neutral-500/10 text-neutral-500" : "bg-ai/10 text-ai"}`}>
                      {c.status === "scheduled" && <Clock className="h-3 w-3" />} {c.status}
                    </span>
                  </TableCell>
                  <TableCell>{c.recipient_count ?? "—"}</TableCell>
                  <TableCell>{c.sent_count}</TableCell>
                  <TableCell data-testid={`campaign-opens-${c.id}`}>
                    <span className="font-semibold text-neutral-900">{c.opened_count}</span>
                    <span className="ml-1 text-xs text-neutral-400">({c.open_rate}%)</span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-neutral-500">
                    {c.scheduled && c.status === "scheduled"
                      ? new Date(c.send_at).toLocaleString()
                      : (c.sent_at || c.created_at || "").slice(0, 16).replace("T", " ")}
                  </TableCell>
                  <TableCell className="text-right">
                    {c.status === "scheduled" && (
                      <Button size="sm" variant="outline" onClick={() => cancelCampaign(c.id)} disabled={canceling === c.id}
                        className="h-8 gap-1 rounded-lg" data-testid={`campaign-cancel-${c.id}`}>
                        {canceling === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ban className="h-3.5 w-3.5" />} Cancel
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <Input placeholder="Search recipient or subject…" value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()} className="h-11 rounded-xl pl-9" data-testid="email-search" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="h-11 w-52 rounded-xl" data-testid="email-cat-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="launch_announcement">Launch announcement</SelectItem>
            <SelectItem value="broadcast">Broadcast</SelectItem>
            <SelectItem value="waitlist_confirmation">Waitlist confirmation</SelectItem>
            <SelectItem value="provider_confirmation">Provider confirmation</SelectItem>
            <SelectItem value="general">General</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-11 w-36 rounded-xl" data-testid="email-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="sent">Delivered</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="skipped">Skipped</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={load} className="h-11 rounded-xl bg-brand hover:bg-brand-dark" data-testid="email-apply">Search</Button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-black/5 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Recipient</TableHead><TableHead>Subject</TableHead>
              <TableHead>Type</TableHead><TableHead>Status</TableHead><TableHead>Sent</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="py-10 text-center text-neutral-400">No emails yet. Confirmation emails appear here as people join.</TableCell></TableRow>
            )}
            {data.rows.map((r) => (
              <TableRow key={r.id} data-testid="email-row">
                <TableCell className="font-medium">{r.to}</TableCell>
                <TableCell className="max-w-[240px] truncate text-neutral-600">{r.subject}</TableCell>
                <TableCell><Badge variant="secondary">{catLabel(r.category)}</Badge></TableCell>
                <TableCell><span className={`rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${STATUS_STYLE[r.status] || "bg-neutral-100 text-neutral-600"}`}>{r.status}</span></TableCell>
                <TableCell className="whitespace-nowrap text-neutral-500">{r.created_at?.slice(0, 16).replace("T", " ")}</TableCell>
                <TableCell>
                  <div className="flex justify-end gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => openPreview(r.id)} className="h-8 gap-1 rounded-lg" data-testid={`email-preview-${r.id}`}><Eye className="h-3.5 w-3.5" /> Preview</Button>
                    <Button size="sm" variant="outline" onClick={() => resend(r.id)} disabled={resending === r.id} className="h-8 gap-1 rounded-lg" data-testid={`email-resend-${r.id}`}>
                      {resending === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Resend
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="mt-3 text-sm text-neutral-400">{data.total} emails logged</p>

      <Dialog open={Boolean(preview)} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl" data-testid="email-preview-dialog">
          <DialogHeader>
            <DialogTitle className="truncate">{preview?.subject}</DialogTitle>
            <DialogDescription>To {preview?.to} · {catLabel(preview?.category)} · {preview?.status}{preview?.error ? ` · ${preview.error}` : ""}</DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="max-h-[60vh] overflow-auto rounded-xl border border-black/5">
              <iframe title="Email preview" srcDoc={preview.html} className="h-[520px] w-full" sandbox="" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <EmailComposer
        open={Boolean(composer)}
        preset={composer}
        onClose={() => setComposer(null)}
        onSent={refreshAll}
      />
    </div>
  );
}
